import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type * as LeafletNS from 'leaflet';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  useProperty,
  usePropertyField,
} from '@vibesites/runtime/react';
import {
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { pickHighestContrast, contrastRatio, relativeLuminance } from '../utils/luminance';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Location CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Flat neighborhood map (Leaflet + OpenStreetMap)
//
// A clean, basic 2D roadmap with a single property pin — rendered with
// Leaflet using CARTO's free "positron" raster tiles (OSM data). No API
// key, no billing, no per-load charges. Requires only attribution.
//
// Leaflet is loaded dynamically at runtime so its `window` references
// never run during SSG/lint.
// ============================================================================

const LEAFLET_CSS_HREF = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';

function ensureLeafletCss() {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[data-leaflet-css]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = LEAFLET_CSS_HREF;
  link.setAttribute('data-leaflet-css', '');
  document.head.appendChild(link);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string),
  );
}

// Teardrop property pin in the brand primary color with a house glyph.
function makePropertyPin(primaryColor: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="74" viewBox="0 0 60 74">
  <ellipse cx="30" cy="70" rx="12" ry="4" fill="rgba(0,0,0,0.2)"/>
  <path d="M30 0C13.43 0 0 13.43 0 30c0 22.5 30 42 30 42s30-19.5 30-42C60 13.43 46.57 0 30 0z" fill="${primaryColor}" stroke="#fff" stroke-width="2.5"/>
  <circle cx="30" cy="27" r="18" fill="#fff" opacity="0.95"/>
  <path d="M24 26v10h4v-5h4v5h4V26l-6-5z" fill="${primaryColor}"/>
  <path d="M21 27l9-8 9 8" fill="none" stroke="${primaryColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`)}`;
}

function FlatPropertyMap() {
  const property = useProperty();
  const latRaw = usePropertyField('property_address_latitude');
  const lngRaw = usePropertyField('property_address_longitude');
  const propertyName = (property?.property_name as string) || '';
  const propertyAddress = (usePropertyField('property_full_address') as string) || '';
  const primaryColor = (property?.property_primary_color as string) || '#4a7c59';

  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  const lat = parseFloat((latRaw as string) || '');
  const lng = parseFloat((lngRaw as string) || '');
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  useEffect(() => {
    if (!hasCoords || !containerRef.current) return;
    if (typeof window === 'undefined') return;

    let cancelled = false;
    let map: LeafletNS.Map | null = null;
    ensureLeafletCss();

    import('leaflet')
      .then((mod) => {
        if (cancelled || !containerRef.current) return;
        const L: typeof LeafletNS = (mod as any).default ?? mod;

        map = L.map(containerRef.current, {
          center: [lat, lng],
          zoom: 14,
          zoomControl: false,
          scrollWheelZoom: false,
          attributionControl: true,
        });

        // Zoom control on the right so the pin/left copy area stays clean,
        // matching the previous Google Maps layout.
        L.control.zoom({ position: 'bottomright' }).addTo(map);

        // CARTO "positron" — free, no-key, light flat basemap using OSM data.
        L.tileLayer(
          'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
          {
            maxZoom: 19,
            subdomains: 'abcd',
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          },
        ).addTo(map);

        const icon = L.icon({
          iconUrl: makePropertyPin(primaryColor),
          iconSize: [48, 59],
          iconAnchor: [24, 59],
          popupAnchor: [0, -55],
        });

        const marker = L.marker([lat, lng], {
          icon,
          title: propertyName || 'Property',
        }).addTo(map);

        if (propertyName || propertyAddress) {
          const content = `<div style="font-family:var(--vibe-font-body,sans-serif);max-width:220px;padding:2px 4px;">
            <div style="font-size:14px;font-weight:600;color:#1a1a1a;line-height:1.3;">${escapeHtml(propertyName || 'Property')}</div>
            ${propertyAddress ? `<div style="margin-top:4px;font-size:12px;line-height:1.5;color:#555;">${escapeHtml(propertyAddress)}</div>` : ''}
          </div>`;
          marker.bindPopup(content);
        }

        setReady(true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (map) map.remove();
    };
  }, [lat, lng, hasCoords, primaryColor, propertyName, propertyAddress]);

  return (
    <div className="flat-map">
      {hasCoords ? (
        <div ref={containerRef} className="flat-map__canvas" />
      ) : (
        <div className="flat-map__empty">Map coordinates not available</div>
      )}
      {hasCoords && !ready && <div className="flat-map__loading">Loading map…</div>}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);

  // The section sits on the secondary color, which a property can override to
  // anything. Pick a legible text color against the actual resolved secondary
  // surface: prefer the theme's `textOnSecondary` (WCAG-tuned by the tokens
  // generator) with the body `text` color as the light-secondary alternative,
  // and fall back to synthesized pure black/white if neither hits WCAG AA —
  // so heading and body copy stay readable under any property override.
  const tokens = useTokens();
  const textColor = useMemo(() => {
    const c = tokens?.color;
    const sBg = c?.secondary || '#3E441D';
    const dark = c?.text || '#000000';
    const picked = pickHighestContrast(sBg, [c?.textOnSecondary || '#FFFFFF', dark]);
    if (picked && contrastRatio(sBg, picked) >= 4.5) return picked;
    return relativeLuminance(sBg) > 0.5 ? '#000000' : '#FFFFFF';
  }, [tokens]);

  useRevealStyles();
  useComfyCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // This section sits ON the secondary color, so the default secondary-fill CTA
  // would be invisible. Flip the resting fill to primary and swap to the page
  // background color on hover so the button stays legible against the section.
  const ctaColorVars = {
    '--comfy-cta-bg': 'var(--vibe-color-primary)',
    '--comfy-cta-fg': 'var(--vibe-color-text-on-primary)',
    '--comfy-cta-bg-hover': 'var(--vibe-color-background)',
    '--comfy-cta-fg-hover': 'var(--vibe-color-text)',
  } as CSSProperties;

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up relative w-full overflow-hidden bg-secondary py-14 md:py-20 lg:py-24"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:px-12">
        {/* Left: copy */}
        <div className="relative z-10 max-w-xl lg:pl-4 xl:pl-10">
          <RichTextContent
            path="neighborhoodsection.heading"
            label="Heading"
            defaultValue="<p>The heart of a growing community</p>"
            as="h2"
            className="font-heading mb-6 text-3xl font-bold uppercase leading-[1.1] sm:text-4xl md:text-[2.75rem]"
            style={{ color: textColor }}
          />

          <RichTextContent
            path="neighborhoodsection.body"
            label="Body Text"
            defaultField="property_home_neighborhood_blurb"
            defaultValue="<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>"
            as="div"
            className="font-body mb-10 max-w-lg text-base leading-relaxed [&_a]:text-primary [&_a]:underline"
            style={{ color: textColor }}
          />

          <EditableSlotGroup
            groupPath="neighborhoodsection.cta_link"
            definition={ctaLinkDef}
            className="inline-block"
          >
            <a
              href={cta.url || '/neighborhood'}
              target={cta._target || '_self'}
              className="neighborhood-cta comfy-cta"
              style={ctaColorVars}
            >
              <RichTextContent
                path="neighborhoodsection.cta_label"
                label="CTA Label"
                defaultValue="<p>Explore the Location</p>"
                as="span"
              />
            </a>
          </EditableSlotGroup>
        </div>

        {/* Right: flat neighborhood map */}
        <div className="relative z-10 w-full">
          <div className="neighborhood-map block w-full">
            <FlatPropertyMap />
          </div>
        </div>
      </div>

      <style>{`
        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        .neighborhood-map {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-radius: 4px;
          background: #fff;
          box-shadow: 0 10px 30px rgba(52, 46, 31, 0.12);
        }

        .flat-map {
          position: relative;
          width: 100%;
          height: 420px;
        }
        @media (min-width: 768px) {
          .flat-map { height: 500px; }
        }
        .flat-map__canvas {
          position: absolute;
          inset: 0;
        }
        .flat-map__empty,
        .flat-map__loading {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f5f5;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>The heart of a growing community</p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore the Location</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
